import type {
  ActionEnvelope,
  AgentSelection,
  ChatInputAnswer,
  ChatInputRequest,
  ChatState,
  CompletionItem,
  MessageAttachment,
  ModelSelection,
  PendingMessage,
  SessionState,
  ToolCallResult,
  URI,
} from '@microsoft/agent-host-protocol'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import type {AcquireRelayLeaseOptions, RelayLeaseIdentity} from '@github-ui/ahp-relay/relay-registry'
import type {RelaySteeringMessage} from '@github-ui/ahp-relay/relay-steering-messages'
import type {
  ClientTool,
  RelayChatObserver,
  RelayChangesetObserver,
  RelayModelCallFailure,
  RelayStatus,
  RelayTelemetryObserver,
} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'
import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'

/** The message metadata namespace used for the browser-contributed per-turn instruction snapshot. */
export const CONTRIBUTED_INSTRUCTIONS_META_KEY = 'github.contributedInstructions' as const

export type ContributedInstructionKind = 'personal' | 'repository' | 'organization'

/**
 * A metadata-only description of one instruction set contributed by the browser.
 * Prompt text is intentionally not part of this contract.
 */
export interface ContributedInstructionMetadata {
  readonly kind: ContributedInstructionKind
  readonly label?: string
  readonly ownerUrl?: string
}

export type ContributedInstructionsMetadata = readonly ContributedInstructionMetadata[]

export interface EstablishedAhpSessionIdentity extends RelayLeaseIdentity {
  environmentId: string
  sessionId: string
}

/**
 * The three ids that name a connectable AHP session: the task it belongs to,
 * the environment it runs in, and the session itself.
 *
 * This lives beside the session contracts rather than in a UI package because
 * every layer that talks about "which session" — the composer, the shell, the
 * history list, the relay bootstrap — needs the same shape, and none of them
 * should have to reach up into a product package to name it.
 */
export interface AhpSessionIdentifiers {
  taskId: string
  environmentId: string
  sessionId: string
  channel?: string
}

/**
 * Identity used by passive history UI without implying that compute is
 * connectable. A row in a list knows its task but may not know, or need, a live
 * environment or session.
 */
export type AhpSessionDisplayIdentity = Pick<AhpSessionIdentifiers, 'taskId'> &
  Partial<Pick<AhpSessionIdentifiers, 'environmentId' | 'sessionId'>>

export interface PreparedPrompt {
  readonly text: string
  readonly attachments?: readonly MessageAttachment[]
  readonly model?: ModelSelection
  readonly agent?: AgentSelection
  readonly contributedInstructions?: ContributedInstructionsMetadata
}

/** Creates the canonical prompt shape at a host boundary without carrying empty attachments. */
export function createPreparedPrompt(input: PreparedPrompt): PreparedPrompt {
  const attachments = input.attachments && input.attachments.length > 0 ? [...input.attachments] : undefined
  const contributedInstructions = input.contributedInstructions ? [...input.contributedInstructions] : undefined
  return {
    text: input.text,
    ...(attachments ? {attachments} : {}),
    ...(input.model ? {model: input.model} : {}),
    ...(input.agent ? {agent: input.agent} : {}),
    ...(contributedInstructions ? {contributedInstructions} : {}),
  }
}

/**
 * Lower bound on what a prompt costs to store, in UTF-16 code units — the unit
 * browsers charge web storage against.
 *
 * Images and documents travel as base64 `data`, which dominates the total, so
 * the sum is taken from the strings already in memory rather than by copying or
 * serializing the prompt. Callers that need an exact figure measure the
 * serialized record instead; this is the cheap check that runs on the Send
 * interaction.
 */
export function preparedPromptPayloadChars(prompt: PreparedPrompt): number {
  let total = prompt.text.length
  for (const attachment of prompt.attachments ?? []) {
    total += attachment.label?.length ?? 0
    const data = (attachment as {data?: unknown}).data
    if (typeof data === 'string') total += data.length
  }
  return total
}

export interface EstablishedAhpPendingMessage {
  readonly id: string
  readonly prompt: PreparedPrompt
  readonly kind: 'queued' | 'steering'
}

function decodeContributedInstructions(raw: unknown): ContributedInstructionsMetadata | undefined {
  if (!Array.isArray(raw)) return undefined

  const instructions: ContributedInstructionMetadata[] = []
  for (const value of raw) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue

    const record = value as Record<string, unknown>
    const kind =
      record.kind === 'personal' || record.kind === 'repository' || record.kind === 'organization'
        ? record.kind
        : undefined
    if (!kind) continue

    instructions.push({
      kind,
      ...(typeof record.label === 'string' ? {label: record.label} : {}),
      ...(typeof record.ownerUrl === 'string' ? {ownerUrl: record.ownerUrl} : {}),
    })
  }

  return instructions.length > 0 ? instructions : undefined
}

export function createEstablishedAhpPendingMessage(message: PendingMessage): EstablishedAhpPendingMessage {
  const source = message.message
  const contributedInstructions = decodeContributedInstructions(source._meta?.[CONTRIBUTED_INSTRUCTIONS_META_KEY])

  return {
    id: message.id,
    kind: 'queued',
    prompt: {
      text: source.text,
      ...(source.attachments ? {attachments: source.attachments} : {}),
      ...(source.model ? {model: source.model} : {}),
      ...(source.agent ? {agent: source.agent} : {}),
      ...(contributedInstructions ? {contributedInstructions} : {}),
    },
  }
}

export interface DispatchReceipt {
  readonly requestId: string
  readonly clientSeq: number
  readonly turnId?: string
  readonly pendingMessageId?: string
}

export type DirectTurnKind = 'new_chat' | 'existing_chat'
export type DirectTurnStartPath = 'cold' | 'resume' | 'active'

export interface DirectTurnSubmission {
  readonly requestId: string
  readonly turnKind: DirectTurnKind
  readonly startPath: DirectTurnStartPath
  readonly modelId?: string
}

/**
 * Optional browser-local observer for one product's direct-turn telemetry.
 * Queue commands bypass this contract. Steering begins a provisional attempt
 * in case preparation outlives the active turn, then discards it if still steering.
 */
export interface EstablishedAhpDirectTurnTelemetry {
  readinessChanged(ready: boolean): void
  beginSubmission(submission: DirectTurnSubmission): void
  discardSubmission(requestId: string): void
  readinessRequired(requestId: string): void
  readinessObserved(requestId: string): void
  turnDispatching(requestId: string): void
  turnDispatched(requestId: string, turnId: string): void
  preDispatchError(requestId: string): void
  acceptedAction(envelope: ActionEnvelope, previousState: ChatState | undefined, state: ChatState | undefined): void
  reset(): void
}

export interface StopOutcome {
  readonly accepted: boolean
  readonly turnId?: string
}

export interface EstablishedAhpSessionView {
  getSnapshot(): EstablishedAhpSessionViewSnapshot
  subscribe(listener: () => void): () => void
  observeChat(channel: string, observer: RelayChatObserver): () => void
  observeChangeset?(channel: string, observer: RelayChangesetObserver): () => void
  subscribeTelemetry?(observer: RelayTelemetryObserver): Promise<() => void>
}

export interface EstablishedAhpSessionViewSnapshot {
  readonly state: ChatState | undefined
  readonly status: RelayStatus | 'idle'
  readonly reachable: boolean
  readonly error: string | undefined
  readonly failure?: RelayFailure
  readonly modelFailures: Record<string, RelayModelCallFailure> | undefined
  readonly provisioning: ProvisioningStatus | undefined
  readonly sessionState: SessionState | undefined
  readonly passive: boolean
  readonly passiveKnown: boolean
  readonly steeringMessages: readonly RelaySteeringMessage[]
}

export interface EstablishedAhpSessionInputController {
  submit(prompt: PreparedPrompt, requestId?: string, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt>
  queue(prompt: PreparedPrompt, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt>
  /** Steers the live active turn, or submits a new turn if idle after preparation. */
  steer(prompt: PreparedPrompt, steeringId?: string, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt>
  sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt>
  removeQueued(id: string): Promise<void>
}

export interface ToolConfirmationResponse {
  readonly approved: boolean
  readonly toolCallId: string
  readonly turnId: string
  readonly resultConfirmation?: boolean
  readonly selectedOptionId?: string
  readonly forSession?: boolean
  /** Chat channel that owns a session-level confirmation. */
  readonly chat?: URI
}

export type EstablishedAhpPromptCommandType =
  | 'ask_user_response'
  | 'elicitation_response'
  | 'permission_response'
  | 'plan_approval_response'

export interface EstablishedAhpPrompt {
  readonly toolCallId: string
  readonly toolName: string
  readonly arguments: unknown
}

export interface EstablishedAhpToolConfirmationOption {
  readonly id: string
  readonly label: string
  readonly kind: 'approve' | 'deny'
  readonly selectedOptionId?: string
  readonly forSession?: boolean
}

export interface EstablishedAhpToolConfirmation {
  readonly turnId: string
  readonly toolCallId: string
  readonly title: string
  readonly detail?: string
  readonly options: readonly EstablishedAhpToolConfirmationOption[]
  readonly resultConfirmation: boolean
  /** Originating chat for a session-level confirmation. */
  readonly chat?: URI
}

export interface InputCompletion {
  readonly requestId: string
  readonly response: 'accept' | 'decline' | 'cancel'
  readonly answers?: Record<string, ChatInputAnswer>
}

export interface EstablishedAhpSessionCommands {
  cancel(turnId?: string): StopOutcome
  claimActiveClient(tools: readonly ClientTool[]): Promise<void>
  completions(text: string, offset: number): Promise<CompletionItem[]>
  respondToPrompt(input: {
    requestId: string
    response: 'accept' | 'decline' | 'cancel'
    answers?: InputCompletion['answers']
  }): void
  confirmTool(input: ToolConfirmationResponse): void
  completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void
  renameSession(title: string): void
  setApprovalMode(mode: SessionApprovalMode): Promise<void>
  setApproveAll(enabled: boolean): Promise<void>
  setMode(mode: string): Promise<void>
}

export interface EstablishedAhpPlanningState {
  readonly status: 'idle' | 'pending' | 'approved' | 'rejected' | 'failed'
  readonly plan?: EstablishedAhpPlan
  readonly error?: string
}

export interface EstablishedAhpPlan {
  readonly id: string
  readonly title?: string
  readonly summary?: string
  readonly steps: readonly string[]
}

export interface EstablishedAhpPlanningAdapter {
  identify(request: ChatInputRequest): EstablishedAhpPlan | undefined
  approve(plan: EstablishedAhpPlan): Promise<void>
  reject(plan: EstablishedAhpPlan): Promise<void>
  revise(plan: EstablishedAhpPlan, prompt: PreparedPrompt): Promise<void>
  continue(plan: EstablishedAhpPlan): Promise<void>
}

export interface EstablishedAhpPlanningPolicy {
  readonly enabled: boolean
  readonly adapter?: EstablishedAhpPlanningAdapter
}

export interface EstablishedAhpSessionPlanning {
  readonly state: EstablishedAhpPlanningState
  readonly actions: {
    approve(): Promise<void>
    reject(): Promise<void>
    revise(prompt: PreparedPrompt): Promise<void>
    continue(): Promise<void>
  }
}

export type EstablishedAhpLifecycleState = 'connecting' | 'waking' | 'live' | 'reconnecting' | 'failed' | 'disposed'

export interface EstablishedAhpSessionLifecycle {
  getState(): EstablishedAhpLifecycleState
  subscribe(listener: () => void): () => void
}

export interface EstablishedAhpSession {
  readonly identity: EstablishedAhpSessionIdentity
  readonly view: EstablishedAhpSessionView
  readonly commands: EstablishedAhpSessionCommands
  readonly input: EstablishedAhpSessionInputController
  readonly planning?: EstablishedAhpSessionPlanning
  readonly lifecycle: EstablishedAhpSessionLifecycle
}

export interface EstablishedAhpSessionIntegrationServices {
  readonly references?: {
    resolve?: (reference: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly attachments?: {
    prepare?: (attachment: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly repository?: {
    getCurrent?: () => unknown
    select?: (repository: unknown) => void
  }
  readonly spaces?: {
    select?: (space: unknown) => void
  }
}

export interface EstablishedAhpSessionIntegrations extends EstablishedAhpSessionIntegrationServices {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly lifecycle: EstablishedAhpSessionLifecycle
  readonly planning?: EstablishedAhpSessionPlanning
}

export interface FirstTurnHandoff {
  readonly requestId: string
  readonly prompt: PreparedPrompt
  readonly origin: 'new' | 'recovery'
  /** Applies session settings before the first user turn is dispatched. */
  readonly configureSession?: (session: EstablishedAhpSession, prompt?: PreparedPrompt) => Promise<void>
  /** Persists caller-owned state immediately before the relay sends the first turn. */
  readonly beforeDispatch?: () => Promise<void>
  /** Resolves session-advertised prompt metadata before the first user turn is dispatched. */
  readonly preparePrompt?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<PreparedPrompt>
}

export interface AhpSpaceSelection {
  readonly owner: string
  readonly id: number
  readonly slug: string
  readonly name: string
}

/**
 * Submission target metadata. `scope` may accompany `repository` as an owner fallback when
 * repository binding is disabled.
 */
export interface AhpSubmissionMetadata {
  readonly [key: string]: unknown
  readonly space?: {
    readonly owner: string
    readonly slug: string
  }
  readonly repository?: string
  /** Explicit composer selection, interpreted as binding intent only by environment-task provisioning. */
  readonly selectedRepository?: string
  /** Owner login scoping a sandbox provisioning request when no repository is attached. */
  readonly scope?: string
}

/**
 * Where a chat turn should be provisioned. Discriminated so the one decision a
 * composer makes — Space, repository, or owner scope — is made once, at the
 * point of choice, rather than re-derived from optional fields at each hop.
 */
export type AhpProvisioningTarget =
  | {readonly kind: 'space'; readonly space: AhpSpaceSelection}
  | {readonly kind: 'repository'; readonly nameWithOwner: string; readonly scopeOwner?: string}
  | {readonly kind: 'scope'; readonly owner: string}

export interface AhpChatStartRequest {
  readonly prompt: PreparedPrompt
  /** The single resolved destination carried across hosts and navigation handoffs. */
  readonly provisioningTarget?: AhpProvisioningTarget
  readonly metadata?: AhpSubmissionMetadata
  /** Full picker state retained by the host while provisioning locks the Space. */
  readonly initialSelectedSpace?: AhpSpaceSelection
}

/**
 * Builds a start request from a resolved provisioning target.
 *
 * A selected Space travels twice on purpose: as the `{owner, slug}`
 * provisioning needs, and as the full picker selection the destination
 * composer shows as its locked Space.
 */
export function createAhpChatStartRequest({
  prompt,
  target,
}: {
  prompt: PreparedPrompt
  target?: AhpProvisioningTarget
}): AhpChatStartRequest {
  if (target?.kind === 'space') {
    return {
      prompt,
      metadata: {space: {owner: target.space.owner, slug: target.space.slug}},
      initialSelectedSpace: target.space,
    }
  }
  if (target?.kind === 'repository') {
    return {
      prompt,
      metadata: {
        repository: target.nameWithOwner,
        ...(target.scopeOwner ? {scope: target.scopeOwner} : {}),
      },
    }
  }
  if (target?.kind === 'scope') {
    return {prompt, metadata: {scope: target.owner}}
  }
  return {prompt}
}

export function createAhpChatStartRequestWithProvisioningTarget({
  prompt,
  target,
}: {
  prompt: PreparedPrompt
  target?: AhpProvisioningTarget
}): AhpChatStartRequest {
  const request = createAhpChatStartRequest({prompt, target})
  return target ? {...request, provisioningTarget: target} : request
}
export interface ProvisionRequest {
  readonly firstTurn?: FirstTurnHandoff
  readonly metadata?: AhpSubmissionMetadata
}

export interface EstablishedAhpResumptionAttempt {
  readonly signal: AbortSignal
  readonly phase: EstablishedAhpResumptionPhase
  readonly timeoutMs: number
  readonly expiresAt: number
  readonly isDisposed: boolean
  remainingMs(): number
  advanceTo(phase: Exclude<EstablishedAhpResumptionPhase, 'initial'>): void
  waitFor<T>(work: Promise<T>): Promise<T>
  cancel(reason?: Error): void
  dispose(): void
}

export type EstablishedAhpResumptionPhase = 'initial' | 'waking' | 'connecting'

export type EstablishRequest =
  | {
      readonly mode: 'provision'
      readonly input: ProvisionRequest
      /** Fires after provisioning resolves identity, before relay startup or first-turn acceptance. */
      readonly onIdentityResolved?: (identity: EstablishedAhpSessionIdentity) => void
    }
  | {
      /**
       * Retries relay/session establishment from the result of the latest
       * provision without invoking the product provisioning adapter again.
       */
      readonly mode: 'resume-provision'
      readonly firstTurn?: FirstTurnHandoff
    }
  | {
      readonly mode: 'attach'
      readonly identity: EstablishedAhpSessionIdentity
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
    }
  | {
      readonly mode: 'wake'
      readonly identity: EstablishedAhpSessionIdentity
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
    }
  | {
      readonly mode: 'recover'
      readonly identity: EstablishedAhpSessionIdentity
      readonly firstTurn?: FirstTurnHandoff
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
    }

export interface EstablishedAhpSessionProvisioned {
  readonly identity: EstablishedAhpSessionIdentity
  readonly relay: AcquireRelayLeaseOptions
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
}

export interface EstablishedAhpSessionBootstrapAdapter {
  provision(input: ProvisionRequest): Promise<EstablishedAhpSessionProvisioned>
  attach(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  wake(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  recover(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  reset?(): void
  dispose?(): void
}

export const ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS = 120_000

export type EstablishedAhpBootstrapState =
  | 'unbound'
  | 'provisioning'
  | 'history'
  | 'connecting'
  | 'waking'
  | 'reconnecting'
  | 'failed'
  | 'established'
  | 'disposed'

export interface EstablishedAhpSessionBootstrap {
  readonly state: EstablishedAhpBootstrapState
  readonly session: EstablishedAhpSession | undefined
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  /** Identity retained after provisioning so a failed establishment can resume without provisioning again. */
  readonly provisionedIdentity: EstablishedAhpSessionIdentity | undefined
  /**
   * Installs host-owned relay diagnostics before a new connection is created.
   * Existing relay entries retain the callbacks from their original acquire.
   */
  configureRelayDiagnostics?(diagnostics: EstablishedAhpSessionRelayDiagnostics): void
  establish(request: EstablishRequest): Promise<EstablishedAhpSession>
  /**
   * Accepts cancellation for the active first-turn handoff before the session is
   * published. The exact dispatched turn id is returned once it is available.
   */
  cancelPendingFirstTurn(): StopOutcome
  reset(): void
  dispose(): void
}

export type EstablishedAhpSessionRelayDiagnostics = Pick<AcquireRelayLeaseOptions, 'webSocketFactory' | 'onStatus'>

export class EstablishedAhpSessionError extends Error {
  readonly operation: string
  readonly phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState
  readonly recoverable: boolean
  readonly failure: RelayFailure | undefined

  constructor(
    operation: string,
    phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState,
    message: string,
    recoverable: boolean,
    options?: ErrorOptions & {failure?: RelayFailure},
  ) {
    super(message, options)
    this.name = 'EstablishedAhpSessionError'
    this.operation = operation
    this.phase = phase
    this.recoverable = recoverable
    this.failure = options?.failure
  }
}

export class EstablishedAhpSessionResumptionTimeoutError extends EstablishedAhpSessionError {
  constructor(timeoutMs = ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS) {
    super('resume', 'reconnecting', `The existing AHP session did not become ready within ${timeoutMs}ms.`, true, {
      failure: {kind: 'resumption-timeout'},
    })
    this.name = 'EstablishedAhpSessionResumptionTimeoutError'
  }
}
